import { buildStorefrontMediaProxyUrl } from '../lib/image-proxy';
import {
  mapStorefrontMediaAssetError,
  validateStorefrontMediaFile,
} from '../lib/storefront-engine/storefront-media-validation';
import type { SupabaseClient } from '../supabase/client';
import type {
  PublicMediaReference,
  PublicMediaRole,
  StorefrontMediaAssetErrorCode,
  StorefrontMediaAssetOption,
  StorefrontMediaAssetRemoveInput,
  StorefrontMediaAssetUploadInput,
} from '../types/storefront-engine';

export interface ListStorefrontMediaAssetsInput {
  readonly storeId: string;
  readonly search?: string | null;
  readonly role?: PublicMediaRole;
  readonly limit?: number;
  readonly supabaseUrl: string;
}

export interface StorefrontMediaAssetsService {
  list(
    client: SupabaseClient,
    input: ListStorefrontMediaAssetsInput,
  ): Promise<StorefrontMediaAssetOption[]>;
  upload(
    client: SupabaseClient,
    input: StorefrontMediaAssetUploadInput & { readonly supabaseUrl: string },
  ): Promise<StorefrontMediaAssetOption>;
  remove(
    client: SupabaseClient,
    input: StorefrontMediaAssetRemoveInput,
  ): Promise<{ readonly mediaAssetId: string }>;
  toPublicReference(option: StorefrontMediaAssetOption): PublicMediaReference;
  mapError(error: unknown): StorefrontMediaAssetErrorCode;
}

interface RpcClient {
  rpc(
    name: string,
    args: Record<string, unknown>,
  ): Promise<{ readonly data: unknown; readonly error: unknown }>;
  functions: {
    invoke(
      name: string,
      options: {
        readonly body?: BodyInit | Record<string, unknown>;
        readonly headers?: Record<string, string>;
      },
    ): Promise<{ readonly data: unknown; readonly error: unknown }>;
  };
  auth: {
    getSession(): Promise<{
      readonly data: { readonly session: { readonly access_token: string } | null };
      readonly error: unknown;
    }>;
  };
}

const DEFAULT_ROLE: PublicMediaRole = 'section-image';
const DEFAULT_LIMIT = 40;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function parseBoolean(value: unknown): boolean {
  return value === true;
}

function parseAsset(value: unknown, supabaseUrl: string): StorefrontMediaAssetOption | null {
  const row = asRecord(value);
  const id = typeof row.id === 'string' ? row.id : null;
  const role = row.role === 'section-image' ? row.role : null;
  const status = row.status === 'ready' || row.status === 'blocked' ? row.status : null;
  const createdAt = typeof row.created_at === 'string' ? row.created_at : null;
  if (!id || !role || !status || !createdAt) return null;

  const isSelectable = status === 'ready' && parseBoolean(row.isSelectable);
  return {
    kind: 'media',
    id,
    label: typeof row.label === 'string' && row.label.trim() ? row.label.trim() : null,
    role,
    status,
    image_url: isSelectable ? buildStorefrontMediaProxyUrl(supabaseUrl, id) : null,
    isInUse: parseBoolean(row.isInUse),
    isSelectable,
    created_at: createdAt,
  };
}

function parseAssetArray(value: unknown, supabaseUrl: string): StorefrontMediaAssetOption[] {
  return Array.isArray(value)
    ? value.flatMap((row) => {
        const parsed = parseAsset(row, supabaseUrl);
        return parsed ? [parsed] : [];
      })
    : [];
}

/** The asset of an upload answer. Throws when the answer is not one. */
export function parseStorefrontMediaMutationAsset(
  value: unknown,
  supabaseUrl: string,
): StorefrontMediaAssetOption {
  return parseMutationAsset(value, supabaseUrl);
}

function parseMutationAsset(value: unknown, supabaseUrl: string): StorefrontMediaAssetOption {
  const row = asRecord(value);
  const candidate = 'asset' in row ? row.asset : row;
  const parsed = parseAsset(candidate, supabaseUrl);
  if (!parsed) throw new Error('invalid storefront media asset response');
  return parsed;
}

async function invokeRpc(client: SupabaseClient, name: string, args: object): Promise<unknown> {
  const { data, error } = await (client as unknown as RpcClient).rpc(
    name,
    args as Record<string, unknown>,
  );
  if (error) throw error;
  return data;
}

async function getAccessToken(client: SupabaseClient): Promise<string> {
  const { data, error } = await (client as unknown as RpcClient).auth.getSession();
  if (error) throw error;
  const token = data.session?.access_token;
  if (!token) throw new Error('permission_denied');
  return token;
}

async function invokeFunction(
  client: SupabaseClient,
  body: FormData | Record<string, unknown>,
  storeId: string,
): Promise<unknown> {
  const accessToken = await getAccessToken(client);
  const { data, error } = await (client as unknown as RpcClient).functions.invoke(
    'storefront-media',
    {
      body,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-dukanti-store-id': storeId,
      },
    },
  );
  if (error) throw error;
  return data;
}

export async function listStorefrontMediaAssets(
  client: SupabaseClient,
  input: ListStorefrontMediaAssetsInput,
): Promise<StorefrontMediaAssetOption[]> {
  const data = await invokeRpc(client, 'list_storefront_media_assets_for_admin', {
    target_store_id: input.storeId,
    search_query: input.search?.trim().slice(0, 120) || null,
    target_role: input.role ?? DEFAULT_ROLE,
    page_limit: input.limit ?? DEFAULT_LIMIT,
  });
  return parseAssetArray(data, input.supabaseUrl);
}

export async function uploadStorefrontMediaAsset(
  client: SupabaseClient,
  input: StorefrontMediaAssetUploadInput & { readonly supabaseUrl: string },
): Promise<StorefrontMediaAssetOption> {
  const validation = await validateStorefrontMediaFile(input.file);
  if (typeof validation === 'string') throw new Error(validation);

  const form = new FormData();
  form.set('action', 'upload');
  form.set('store_id', input.storeId);
  form.set('role', input.role ?? DEFAULT_ROLE);
  form.set('image', input.file);
  if (input.label?.trim()) form.set('label', input.label.trim().slice(0, 120));
  if (input.altText?.trim()) form.set('alt_text', input.altText.trim().slice(0, 240));

  return parseMutationAsset(await invokeFunction(client, form, input.storeId), input.supabaseUrl);
}

export async function removeStorefrontMediaAsset(
  client: SupabaseClient,
  input: StorefrontMediaAssetRemoveInput,
): Promise<{ readonly mediaAssetId: string }> {
  const data = asRecord(
    await invokeFunction(
      client,
      {
        action: 'remove',
        store_id: input.storeId,
        media_asset_id: input.mediaAssetId,
      },
      input.storeId,
    ),
  );
  const mediaAssetId = typeof data.mediaAssetId === 'string' ? data.mediaAssetId : null;
  if (!mediaAssetId) throw new Error('remove_failed');
  return { mediaAssetId };
}

export function toStorefrontMediaPublicReference(
  option: StorefrontMediaAssetOption,
): PublicMediaReference {
  if (!option.isSelectable || option.status !== 'ready') throw new Error('invalid_request');
  return {
    kind: 'public-media-reference',
    mediaAssetId: option.id,
    role: option.role,
  };
}

export const storefrontMediaAssetsService: StorefrontMediaAssetsService = {
  list: listStorefrontMediaAssets,
  upload: uploadStorefrontMediaAsset,
  remove: removeStorefrontMediaAsset,
  toPublicReference: toStorefrontMediaPublicReference,
  mapError: mapStorefrontMediaAssetError,
};
